import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { exportToCSV, exportAllData } from '../utils/csvExport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ChartBar,
  Package,
  Warning,
  TrendUp,
  ArrowULeftDown,
  CurrencyDollar,
  Download,
  CircleNotch,
  Receipt,
  Printer,
  Scales
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ReportTable({ columns, data, loading, emptyMessage }) {
  return (
    <div className="bg-[var(--card)] border border-[var(--dm-border)] overflow-hidden rounded-xl">
      <div className="overflow-x-auto">
        <table className="w-full table-dm">
          <thead>
            <tr>{columns.map((col, i) => (
              <th key={i} className={`p-4 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>{col.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length} className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-8 text-center text-[var(--dm-text-muted)]">{emptyMessage}</td></tr>
            ) : (
              data.map((row, i) => (
                <tr key={i}>{columns.map((col, j) => (
                  <td key={j} className={`p-4 ${col.align === 'right' ? 'text-right' : ''} ${col.className || ''}`}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}</tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoicePreview({ sale, settings, onClose }) {
  const printRef = useRef();
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`<html><head><title>Invoice ${sale.invoice_number}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;max-width:400px;margin:0 auto}.header{text-align:center;margin-bottom:20px;border-bottom:2px dashed #000;padding-bottom:10px}.header h1{margin:0;font-size:20px}.header p{margin:5px 0;font-size:12px;color:#666}.info{font-size:12px;margin-bottom:15px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:6px 4px;text-align:left}th{border-bottom:1px solid #000}.total-row td{border-top:2px solid #000;font-weight:bold;font-size:16px}.footer{text-align:center;margin-top:20px;font-size:12px;color:#666;border-top:2px dashed #000;padding-top:10px}</style>
      </head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close(); printWindow.focus(); printWindow.print(); printWindow.close();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white text-black max-w-md max-h-[90vh] overflow-y-auto rounded-xl">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold">Invoice</DialogTitle>
          <Button onClick={handlePrint} className="bg-black text-white rounded-xl h-9"><Printer size={18} className="mr-2" />Print</Button>
        </DialogHeader>
        <div ref={printRef} className="mt-4">
          <div className="header text-center border-b-2 border-dashed pb-4 mb-4">
            <h1 className="text-xl font-bold">{settings?.store_name || 'DukaanMate'}</h1>
            {settings?.address && <p className="text-sm text-gray-600">{settings.address}</p>}
            {settings?.phone && <p className="text-sm text-gray-600">{settings.phone}</p>}
          </div>
          <div className="info text-sm mb-4">
            <p><strong>Invoice:</strong> {sale.invoice_number}</p>
            <p><strong>Date:</strong> {new Date(sale.created_at).toLocaleString()}</p>
            {sale.customer_name && <p><strong>Customer:</strong> {sale.customer_name}</p>}
          </div>
          <table className="w-full text-sm mb-4">
            <thead><tr className="border-b"><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Price</th><th className="text-right py-2">Total</th></tr></thead>
            <tbody>{sale.items?.map((item, i) => (
              <tr key={i}><td className="py-2">{item.product_name}</td><td className="text-right py-2">{item.quantity}</td><td className="text-right py-2">${item.selling_price?.toFixed(2)}</td><td className="text-right py-2">${item.line_total?.toFixed(2)}</td></tr>
            ))}</tbody>
            <tfoot><tr className="total-row"><td colSpan="3" className="text-right py-3 border-t-2">TOTAL:</td><td className="text-right py-3 border-t-2 text-lg">${sale.grand_total?.toFixed(2)}</td></tr></tfoot>
          </table>
          <div className="footer text-center text-sm text-gray-600 border-t-2 border-dashed pt-4">
            <p>{settings?.invoice_footer || 'Thank you for your business!'}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Reports() {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'stock';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  const [stockReport, setStockReport] = useState([]);
  const [lowStockReport, setLowStockReport] = useState([]);
  const [salesReport, setSalesReport] = useState([]);
  const [returnsReport, setReturnsReport] = useState([]);
  const [profitReport, setProfitReport] = useState(null);
  const [movementReport, setMovementReport] = useState([]);
  const [taxReport, setTaxReport] = useState(null);

  // Sales History
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesSettings, setSalesSettings] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [previewSale, setPreviewSale] = useState(null);

  const canExport = hasPermission('export_reports');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/categories`, { withCredentials: true });
        setCategories(res.data);
      } catch (error) { console.error('Error fetching categories:', error); }
    };
    fetchCategories();
  }, []);

  const fetchReport = async (reportType) => {
    if (reportType === 'sales-history') {
      setSalesLoading(true);
      try {
        const [salesRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/sales?limit=100`, { withCredentials: true }),
          axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
        ]);
        setSalesHistory(salesRes.data);
        setSalesSettings(settingsRes.data);
      } catch (error) { console.error('Error:', error); }
      finally { setSalesLoading(false); }
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
      if (category) params.append('category', category);
      const res = await axios.get(`${API_URL}/api/reports/${reportType}?${params}`, { withCredentials: true });
      switch (reportType) {
        case 'stock': setStockReport(res.data); break;
        case 'low-stock': setLowStockReport(res.data); break;
        case 'sales': setSalesReport(res.data); break;
        case 'returns': setReturnsReport(res.data); break;
        case 'profit': setProfitReport(res.data); break;
        case 'stock-movement': setMovementReport(res.data); break;
        case 'tax': setTaxReport(res.data); break;
        default: break;
      }
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(activeTab); }, [activeTab]);

  const [exporting, setExporting] = useState(false);
  const handleExportAll = async () => {
    setExporting(true);
    try {
      const results = await exportAllData(API_URL);
      toast.success(`Exported ${results.length} files`);
    } catch (err) { toast.error('Export failed'); }
    setExporting(false);
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('reports')}</h1>
          <p className="text-[var(--dm-text-muted)] mt-1">{t('businessInsights')}</p>
        </div>
        {canExport && (
          <Button onClick={handleExportAll} disabled={exporting} data-testid="export-all-btn"
            className="bg-black text-white font-semibold hover:bg-black/80 rounded-xl h-11 border border-[var(--dm-border)]">
            {exporting ? <CircleNotch size={18} className="animate-spin mr-2" /> : <Download size={18} className="mr-2" />}
            Export All Data
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-4 p-4 bg-[var(--card)] border border-[var(--dm-border)] rounded-xl">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-xs mb-1 block">{t('fromDate')}</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl w-40" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-xs mb-1 block">{t('toDate')}</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl w-40" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-xs mb-1 block">{t('category')}</Label>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl w-40" data-testid="report-category"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--dm-border)]">
              <SelectItem value="all">{t('allCategories')}</SelectItem>
              {categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={() => fetchReport(activeTab)} data-testid="apply-filters-btn"
            className="bg-black text-white font-semibold rounded-xl border border-[var(--dm-border)]">{t('applyFilters')}</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--card)] border border-[var(--dm-border)] p-1 h-auto flex-wrap">
          <TabsTrigger value="sales-history" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-sales-history">
            <Receipt size={16} className="mr-2" />{t('salesHistory')}
          </TabsTrigger>
          <TabsTrigger value="stock" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <Package size={16} className="mr-2" />{t('stock')}
          </TabsTrigger>
          <TabsTrigger value="low-stock" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <Warning size={16} className="mr-2" />{t('lowStock')}
          </TabsTrigger>
          <TabsTrigger value="sales" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <TrendUp size={16} className="mr-2" />{t('sales')}
          </TabsTrigger>
          <TabsTrigger value="returns" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <ArrowULeftDown size={16} className="mr-2" />{t('returns')}
          </TabsTrigger>
          <TabsTrigger value="profit" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <CurrencyDollar size={16} className="mr-2" />{t('profit')}
          </TabsTrigger>
          <TabsTrigger value="stock-movement" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg">
            <ChartBar size={16} className="mr-2" />{t('movement')}
          </TabsTrigger>
          <TabsTrigger value="tax" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-tax-report">
            <Scales size={16} className="mr-2" />{t('taxReport')}
          </TabsTrigger>
        </TabsList>

        {/* Sales History Tab */}
        <TabsContent value="sales-history" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('salesHistory')}</h3>
          </div>
          <div className="bg-[var(--card)] border border-[var(--dm-border)] overflow-hidden rounded-xl">
            <div className="overflow-x-auto">
              <table className="w-full table-dm">
                <thead><tr>
                  <th className="text-left p-4">{t('invoice')}</th>
                  <th className="text-left p-4">{t('date')}</th>
                  <th className="text-left p-4">Customer</th>
                  <th className="text-right p-4">{t('items')}</th>
                  <th className="text-right p-4">{t('total')}</th>
                  <th className="text-center p-4">{t('actions')}</th>
                </tr></thead>
                <tbody>
                  {salesLoading ? (
                    <tr><td colSpan={6} className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></td></tr>
                  ) : salesHistory.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-[var(--dm-text-muted)]"><Receipt size={48} className="mx-auto mb-2 opacity-50" /><p>No sales yet</p></td></tr>
                  ) : (
                    salesHistory.map((sale) => (
                      <tr key={sale.id}>
                        <td className="p-4 font-mono text-[var(--primary)]">{sale.invoice_number}</td>
                        <td className="p-4 text-[var(--dm-text-muted)]">{new Date(sale.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-[var(--text)]">{sale.customer_name || 'Walk-in'}</td>
                        <td className="p-4 text-right font-mono text-[var(--text)]">{sale.items?.length || 0}</td>
                        <td className="p-4 text-right font-mono text-[var(--primary)]">${sale.grand_total?.toFixed(2)}</td>
                        <td className="p-4 text-center">
                          <Button size="sm" variant="outline" onClick={() => setPreviewSale(sale)} data-testid={`view-invoice-${sale.id}`}
                            className="border-[var(--dm-border)] text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-xl">
                            <Printer size={16} className="mr-1" />{t('view')}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {previewSale && <InvoicePreview sale={previewSale} settings={salesSettings} onClose={() => setPreviewSale(null)} />}
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('currentStockReport')}</h3>
            {canExport && <Button variant="outline" onClick={() => exportToCSV(stockReport, 'stock_report')} data-testid="export-stock-btn" className="border-[var(--dm-border)] text-[var(--primary)] rounded-xl"><Download size={16} className="mr-2" />{t('export')}</Button>}
          </div>
          <ReportTable columns={[
            { label: 'Code', key: 'product_code', render: (r) => <span className="font-mono text-[var(--primary)]">{r.product_code}</span> },
            { label: t('productName'), key: 'name', render: (r) => <span className="text-[var(--text)]">{r.name}</span> },
            { label: t('category'), key: 'category', className: 'text-[var(--dm-text-muted)]' },
            { label: 'Stock', key: 'current_stock', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">{r.current_stock}</span> },
            { label: 'Cost', key: 'cost_price', align: 'right', render: (r) => <span className="font-mono text-[var(--dm-text-muted)]">${r.cost_price?.toFixed(2)}</span> },
            { label: 'Value', key: 'stock_value', align: 'right', render: (r) => <span className="font-mono text-[var(--primary)]">${r.stock_value?.toFixed(2)}</span> },
          ]} data={stockReport} loading={loading} emptyMessage="No stock data" />
        </TabsContent>

        <TabsContent value="low-stock" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('lowStockReport')}</h3>
            {canExport && <Button variant="outline" onClick={() => exportToCSV(lowStockReport, 'low_stock_report')} className="border-[var(--dm-border)] text-[var(--primary)] rounded-xl"><Download size={16} className="mr-2" />{t('export')}</Button>}
          </div>
          <ReportTable columns={[
            { label: 'Code', key: 'product_code', render: (r) => <span className="font-mono text-[#F97316]">{r.product_code}</span> },
            { label: t('productName'), key: 'name', render: (r) => <span className="text-[var(--text)]">{r.name}</span> },
            { label: t('currentStock'), key: 'current_stock', align: 'right', render: (r) => <span className={`font-mono px-2 py-1 rounded ${r.current_stock === 0 ? 'bg-[#EF4444]/20 text-[#EF4444]' : 'bg-[#F97316]/20 text-[#F97316]'}`}>{r.current_stock}</span> },
            { label: t('reorderLevel'), key: 'reorder_level', align: 'right', render: (r) => <span className="font-mono text-[var(--dm-text-muted)]">{r.reorder_level}</span> },
          ]} data={lowStockReport} loading={loading} emptyMessage={t('allStockHealthy')} />
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('salesReport')}</h3>
            {canExport && <Button variant="outline" onClick={() => exportToCSV(salesReport, 'sales_report')} className="border-[var(--dm-border)] text-[var(--primary)] rounded-xl"><Download size={16} className="mr-2" />{t('export')}</Button>}
          </div>
          <ReportTable columns={[
            { label: t('productName'), key: 'product_name', render: (r) => <span className="text-[var(--text)]">{r.product_name}</span> },
            { label: 'Code', key: 'product_code', render: (r) => <span className="font-mono text-[var(--dm-text-muted)]">{r.product_code}</span> },
            { label: 'Qty Sold', key: 'total_quantity', align: 'right', render: (r) => <span className="font-mono text-[var(--primary)]">{r.total_quantity}</span> },
            { label: 'Revenue', key: 'total_revenue', align: 'right', render: (r) => <span className="font-mono text-[var(--primary)]">${r.total_revenue?.toFixed(2)}</span> },
            { label: 'Discount', key: 'total_discount', align: 'right', render: (r) => <span className="font-mono text-[#F97316]">${r.total_discount?.toFixed(2)}</span> },
          ]} data={salesReport} loading={loading} emptyMessage="No sales data" />
        </TabsContent>

        <TabsContent value="returns" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('returnsReport')}</h3>
            {canExport && <Button variant="outline" onClick={() => exportToCSV(returnsReport, 'returns_report')} className="border-[var(--dm-border)] text-[var(--primary)] rounded-xl"><Download size={16} className="mr-2" />{t('export')}</Button>}
          </div>
          <ReportTable columns={[
            { label: 'Return #', key: 'return_number', render: (r) => <span className="font-mono text-[#EF4444]">{r.return_number}</span> },
            { label: t('date'), key: 'created_at', render: (r) => <span className="text-[var(--dm-text-muted)]">{new Date(r.created_at).toLocaleDateString()}</span> },
            { label: t('productName'), key: 'product_name', render: (r) => <span className="text-[var(--text)]">{r.product_name}</span> },
            { label: 'Qty', key: 'quantity_returned', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">{r.quantity_returned}</span> },
            { label: t('reason'), key: 'return_reason', className: 'text-[var(--dm-text-muted)] capitalize' },
            { label: 'Refund', key: 'refund_amount', align: 'right', render: (r) => <span className="font-mono text-[#EF4444]">${r.refund_amount?.toFixed(2)}</span> },
          ]} data={returnsReport} loading={loading} emptyMessage="No returns data" />
        </TabsContent>

        <TabsContent value="profit" className="mt-4">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4">{t('profitSummary')}</h3>
          {loading ? (
            <div className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></div>
          ) : profitReport ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl"><p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('totalRevenue')}</p><p className="text-3xl font-bold font-mono text-[var(--primary)]">${profitReport.total_revenue?.toLocaleString()}</p></div>
              <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl"><p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('totalRefunds')}</p><p className="text-3xl font-bold font-mono text-[#EF4444]">${profitReport.total_refunds?.toLocaleString()}</p></div>
              <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl"><p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('netRevenue')}</p><p className="text-3xl font-bold font-mono text-[var(--primary)]">${profitReport.net_revenue?.toLocaleString()}</p></div>
              <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl"><p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('invoicesReturns')}</p><p className="text-3xl font-bold font-mono text-[var(--text)]">{profitReport.invoice_count} / {profitReport.return_count}</p></div>
            </div>
          ) : <p className="text-[var(--dm-text-muted)] text-center py-8">No profit data</p>}
        </TabsContent>

        <TabsContent value="stock-movement" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">{t('stockMovement')}</h3>
            {canExport && <Button variant="outline" onClick={() => exportToCSV(movementReport, 'stock_movement_report')} className="border-[var(--dm-border)] text-[var(--primary)] rounded-xl"><Download size={16} className="mr-2" />{t('export')}</Button>}
          </div>
          <ReportTable columns={[
            { label: t('date'), key: 'created_at', render: (r) => <span className="text-[var(--dm-text-muted)] text-sm">{new Date(r.created_at).toLocaleString()}</span> },
            { label: 'Type', key: 'type', render: (r) => (
              <span className={`px-2 py-1 text-xs font-medium rounded ${r.type === 'inventory_add' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' : r.type === 'inventory_adjustment' ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#3B82F6]/20 text-[#3B82F6]'}`}>
                {r.type?.replace('inventory_', '').replace('_', ' ')}
              </span>
            )},
            { label: t('productName'), key: 'product_name', render: (r) => <span className="text-[var(--text)]">{r.product_name}</span> },
            { label: 'Change', key: 'change', align: 'right', render: (r) => { const change = r.quantity || r.adjustment_quantity || 0; return <span className={`font-mono ${change >= 0 ? 'text-[var(--primary)]' : 'text-[#EF4444]'}`}>{change >= 0 ? '+' : ''}{change}</span>; }},
            { label: 'New Stock', key: 'new_stock', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">{r.new_stock}</span> },
          ]} data={movementReport} loading={loading} emptyMessage="No stock movement data" />
        </TabsContent>

        {/* Tax Report Tab */}
        <TabsContent value="tax" className="mt-4">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4 flex items-center justify-between">
            {t('taxReport')}
            {taxReport && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => exportToCSV(taxReport.sales_details || [], 'tax_on_sales')} data-testid="export-tax-sales-btn"
                  className="bg-[var(--dm-bg-card-hover)] text-[var(--text)] hover:bg-[var(--dm-border)] text-xs rounded-lg border border-[var(--dm-border)]">
                  <Download size={14} className="mr-1" />Sales Tax
                </Button>
                <Button size="sm" onClick={() => exportToCSV(taxReport.purchase_details || [], 'tax_on_purchases')} data-testid="export-tax-purchases-btn"
                  className="bg-[var(--dm-bg-card-hover)] text-[var(--text)] hover:bg-[var(--dm-border)] text-xs rounded-lg border border-[var(--dm-border)]">
                  <Download size={14} className="mr-1" />Purchase Tax
                </Button>
              </div>
            )}
          </h3>
          {loading ? (
            <div className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></div>
          ) : taxReport ? (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl" data-testid="tax-collected-card">
                  <p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('taxCollected')}</p>
                  <p className="text-3xl font-bold font-mono text-[var(--primary)]">${taxReport.tax_collected?.toFixed(2)}</p>
                  <p className="text-xs text-[var(--dm-text-muted)] mt-1">{taxReport.sale_count} invoices</p>
                </div>
                <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl" data-testid="tax-paid-card">
                  <p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('taxPaidTotal')}</p>
                  <p className="text-3xl font-bold font-mono text-[#F97316]">${taxReport.tax_paid?.toFixed(2)}</p>
                  <p className="text-xs text-[var(--dm-text-muted)] mt-1">{taxReport.purchase_count} purchases</p>
                </div>
                <div className="bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl" data-testid="net-tax-card">
                  <p className="text-[var(--dm-text-muted)] text-sm mb-2">{t('netTax')}</p>
                  <p className={`text-3xl font-bold font-mono ${taxReport.net_tax >= 0 ? 'text-[var(--primary)]' : 'text-[#EF4444]'}`}>${taxReport.net_tax?.toFixed(2)}</p>
                  <p className="text-xs text-[var(--dm-text-muted)] mt-1">{taxReport.net_tax >= 0 ? 'Payable' : 'Credit'}</p>
                </div>
              </div>

              {/* Tax on Sales detail */}
              <div>
                <h4 className="text-[var(--text)] font-semibold mb-3">{t('taxOnSales')}</h4>
                <ReportTable columns={[
                  { label: 'Invoice', key: 'invoice_number', render: (r) => <span className="font-mono text-[var(--primary)]">{r.invoice_number}</span> },
                  { label: 'Date', key: 'created_at', render: (r) => <span className="text-[var(--dm-text-muted)]">{new Date(r.created_at).toLocaleDateString()}</span> },
                  { label: 'Customer', key: 'customer_name', render: (r) => <span className="text-[var(--text)]">{r.customer_name || 'Walk-in'}</span> },
                  { label: 'Sale Total', key: 'grand_total', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">${r.grand_total?.toFixed(2)}</span> },
                  { label: 'Tax Collected', key: 'total_tax', align: 'right', render: (r) => <span className="font-mono text-[var(--primary)]">${r.total_tax?.toFixed(2)}</span> },
                ]} data={taxReport.sales_details || []} loading={false} emptyMessage="No sales tax data" />
              </div>

              {/* Tax on Purchases detail */}
              <div>
                <h4 className="text-[var(--text)] font-semibold mb-3">{t('taxOnPurchases')}</h4>
                <ReportTable columns={[
                  { label: 'Product', key: 'product_name', render: (r) => <span className="text-[var(--text)]">{r.product_name}</span> },
                  { label: 'Date', key: 'created_at', render: (r) => <span className="text-[var(--dm-text-muted)]">{new Date(r.created_at).toLocaleDateString()}</span> },
                  { label: 'Supplier', key: 'supplier_name', render: (r) => <span className="text-[var(--dm-text-muted)]">{r.supplier_name || '-'}</span> },
                  { label: 'Qty', key: 'quantity', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">{r.quantity}</span> },
                  { label: 'Cost', key: 'cost_price', align: 'right', render: (r) => <span className="font-mono text-[var(--text)]">${r.cost_price?.toFixed(2)}</span> },
                  { label: 'Tax Paid', key: 'tax_paid', align: 'right', render: (r) => <span className="font-mono text-[#F97316]">${(r.tax_paid || 0)?.toFixed(2)}</span> },
                ]} data={taxReport.purchase_details || []} loading={false} emptyMessage="No purchase tax data" />
              </div>
            </div>
          ) : <p className="text-[var(--dm-text-muted)] text-center py-8">No tax data</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
